import { createRoutine } from 'redux-saga-routines';

import { AcceptedLocalsType } from 'cosmos-components/dist/i18n';

import { wrapWithModule, RESET_APP_ACTION } from 'src/utils/redux';
import { SciPagesEnum, SciPageState } from './types';
import {
    BrandNames,
    UkBrandNames,
    UsBrandNames,
    CaBrandNames,
} from 'src/enums';
import { SCIDefaultFlowPaths, SCISpecificFlow } from 'src/constants';
import { DotItem } from '../CarousalDots';

const wrapWithNamespace = wrapWithModule('sciPage');

const initialiseCarouselAction = wrapWithNamespace(
    'carouselDots/initialiseCarousel',
);

const setCarouselCurrentPageAction = wrapWithNamespace(
    'carouselDots/setCurrentPage',
);

const setLanguageAction = wrapWithNamespace('carouselDots/setLanguage');

export const setCarouselPageIsCompletedAction = createRoutine(
    wrapWithNamespace('carouselDots/setPageIsCompleted'),
);

export const removeCarouselPageAction = wrapWithNamespace(
    'carouselDots/removeCarouselPageAction',
);

export const addCarouselPageAction = wrapWithNamespace(
    'carouselDots/addCarouselPageAction',
);

const setFlowAsCompletedAction = wrapWithNamespace(
    'carouselDots/setFlowAsCompletedAction',
);

type Types = typeof setCarouselCurrentPageAction;

const getBrandName = () => {
    const lowerCaseUrl = (window?.location?.href ?? '').toLocaleLowerCase();

    if (lowerCaseUrl.includes(UsBrandNames.roadBear)) {
        return BrandNames.roadBear;
    }
    if (lowerCaseUrl.includes(UsBrandNames.elmonte)) {
        return BrandNames.elmonte;
    }
    if (lowerCaseUrl.includes(UkBrandNames.europe)) {
        return BrandNames.europe;
    }
    if (lowerCaseUrl.includes(CaBrandNames.canadream)) {
        return BrandNames.canadream;
    }

    return BrandNames.aunz;
};

const initialState: SciPageState = {
    brandName: getBrandName(),
    carouselDotsState: {
        currentPage: SciPagesEnum.RetrieveSelfCheckInPage,
        flowCompleted: false,
        items: [],
    },
};

const sciPageReducer = (
    state = initialState,
    { type, payload }: { type: Types; payload: any } = {
        type: '',
        payload: {},
    },
): SciPageState => {
    const handler = HANDLERS[type];

    return handler ? handler(state, payload) : state;
};

export const initialiseCarousel = (payload: {
    orgId: string;
    isRelocation: boolean;
    timeslotsAvailable: boolean;
    documentsAvailable: boolean;
}) => {
    return { type: initialiseCarouselAction, payload };
};

export const setCarouselDotsCurrentPage = (currentPage: SciPagesEnum) => {
    return { type: setCarouselCurrentPageAction, payload: currentPage };
};

export const setLanguage = (language: AcceptedLocalsType | undefined) => {
    return { type: setLanguageAction, payload: { language } };
};

export const removeCarouselPage = (page: SciPagesEnum) => {
    return { type: removeCarouselPageAction, payload: page };
};

export const addCarouselPage = (page: SciPagesEnum) => {
    return { type: addCarouselPageAction, payload: page };
};

export const setFlowAsCompleted = () => ({
    type: setFlowAsCompletedAction,
});

const handleInitialiseCarousel = (
    state: SciPageState,
    payload: {
        orgId: string;
        isRelocation: boolean;
        timeslotsAvailable: boolean;
        documentsAvailable: boolean;
    },
): SciPageState => ({
    ...state,
    orgId: payload.orgId,
    isRelocation: payload.isRelocation,
    timeslotsAvailable: payload.timeslotsAvailable,
    documentsAvailable: payload.documentsAvailable,
    carouselDotsState: {
        currentPage: SciPagesEnum.RetrieveSelfCheckInPage,
        items: SCISpecificFlow.reduce(
            (acc, flow) => (!!flow.condition(payload) ? flow.paths : acc),
            SCIDefaultFlowPaths,
        )
            .filter((path) => !path.condition || path.condition(payload))
            .map((path, index) => {
                const dotItem: DotItem = {
                    pageEnum: path.pageEnum,
                    isCompleted: false,
                    label: `${index + 1}`,
                    path: path.path,
                };

                return dotItem;
            }),
        flowCompleted: false,
    },
});

const handleLanguageAction = (
    state: SciPageState,
    payload: { language: AcceptedLocalsType },
): SciPageState => ({
    ...state,
    language: payload.language,
});

const handleCarouselCurrentPageAction = (
    state: SciPageState,
    payload: SciPagesEnum,
): SciPageState => ({
    ...state,
    carouselDotsState: state.carouselDotsState
        ? {
              ...state.carouselDotsState,
              currentPage: payload,
          }
        : undefined,
});

const handleCarouselPageIsCompletedAction = (
    state: SciPageState,
    payload: SciPagesEnum,
): SciPageState => {
    if (!state?.carouselDotsState) {
        return state;
    }

    const pageIndex = state.carouselDotsState.items.findIndex(
        (item) => item.pageEnum === payload,
    );

    if (pageIndex < 0) {
        return state;
    }

    const items = [...state.carouselDotsState.items];
    items[pageIndex].isCompleted = true;
    return {
        ...state,
        carouselDotsState: {
            ...state.carouselDotsState,
            items: items,
        },
    };
};

const handleRemoveCarouselPageAction = (
    state: SciPageState,
    payload: SciPagesEnum,
): SciPageState => {
    if (!state?.carouselDotsState) {
        return state;
    }

    const pageIndex = state.carouselDotsState.items.findIndex(
        (item) => item.pageEnum === payload,
    );
    const currentPageIndex = state.carouselDotsState.items.findIndex(
        (item) => item.pageEnum === state.carouselDotsState?.currentPage,
    );

    if (pageIndex < 0 || pageIndex <= currentPageIndex) {
        return state;
    }

    const items = [
        ...state.carouselDotsState.items.slice(0, pageIndex),
        ...state.carouselDotsState.items.slice(pageIndex + 1).map((item) => ({
            ...item,
            label: (Number(item.label) - 1).toString(),
        })),
    ];

    return {
        ...state,
        carouselDotsState: {
            ...state.carouselDotsState,
            items,
        },
    };
};

const findIndexInCurrentFlowFromOriginalFlow = (
    currentFlow: DotItem[],
    originalFlow: DotItem[],
    page: SciPagesEnum,
): number => {
    let pageIndexInInitialFlow = originalFlow.findIndex(
        (item) => item.pageEnum === page,
    );

    while (pageIndexInInitialFlow > 0) {
        const previousPageFromOriginalFlow =
            originalFlow[pageIndexInInitialFlow - 1];

        const previousPageIndexCurrentFlow = currentFlow.findIndex(
            (item) => item.pageEnum === previousPageFromOriginalFlow.pageEnum,
        );

        if (previousPageIndexCurrentFlow >= 0) {
            return previousPageIndexCurrentFlow;
        }

        pageIndexInInitialFlow--;
    }

    return -1;
};

const handleAddCarouselPageAction = (
    state: SciPageState,
    payload: SciPagesEnum,
): SciPageState => {
    if (!state?.carouselDotsState) {
        return state;
    }

    const pageIndex = state.carouselDotsState.items.findIndex(
        (item) => item.pageEnum === payload,
    );

    if (pageIndex >= 0) {
        console.error(`Cannot add ${payload} already exist in the flow.`);
        return state;
    }

    const originalCarousel = handleInitialiseCarousel(state, {
        documentsAvailable: state.documentsAvailable ?? false,
        isRelocation: state.isRelocation ?? false,
        timeslotsAvailable: state.timeslotsAvailable ?? false,
        orgId: state.orgId ?? '',
    });

    const pageIndexInInitialFlow = (
        originalCarousel.carouselDotsState?.items ?? []
    ).findIndex((item) => item.pageEnum === payload);

    if (pageIndexInInitialFlow < 0) {
        console.error(
            `Cannot add ${payload}, the page wasn't found in the specific flow.`,
            originalCarousel,
        );
        return state;
    }

    const indexToAddPage = findIndexInCurrentFlowFromOriginalFlow(
        state.carouselDotsState.items,
        originalCarousel.carouselDotsState?.items ?? [],
        payload,
    );

    if (indexToAddPage < 0) {
        console.error(`Cannot add ${payload}, counldn't find index to add it.`);
        return state;
    }

    const items = [
        ...state.carouselDotsState.items.slice(0, indexToAddPage + 1),
        originalCarousel.carouselDotsState!.items[pageIndexInInitialFlow],
        ...state.carouselDotsState.items
            .slice(indexToAddPage + 1)
            .map((item) => ({
                ...item,
                isCompleted: false,
                label: (Number(item.label) + 1).toString(),
            })),
    ];

    return {
        ...state,
        carouselDotsState: {
            ...state.carouselDotsState,
            items,
        },
    };
};

const handleSetFlowAsCompletedAction = (state: SciPageState): SciPageState => ({
    ...state,
    carouselDotsState: state.carouselDotsState
        ? {
              ...state.carouselDotsState,
              flowCompleted: true,
          }
        : undefined,
});

const HANDLERS = {
    [initialiseCarouselAction]: handleInitialiseCarousel,
    [setCarouselCurrentPageAction]: handleCarouselCurrentPageAction,
    [setLanguageAction]: handleLanguageAction,
    [setCarouselPageIsCompletedAction.SUCCESS]:
        handleCarouselPageIsCompletedAction,
    [removeCarouselPageAction]: handleRemoveCarouselPageAction,
    [addCarouselPageAction]: handleAddCarouselPageAction,
    [setFlowAsCompletedAction]: handleSetFlowAsCompletedAction,
    [RESET_APP_ACTION]: () => initialState,
};

export default sciPageReducer;
